import { useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { DocumentForm } from '@/apps/engagement-letter-database/components/DocumentForm';
import type { DocumentFormData } from '@/apps/engagement-letter-database/components/DocumentForm/types';
import { markEngagementRecordExecutedAtom } from '@/apps/engagement-letter-database/store';
import {
  useAddEngagementDocumentMutation,
  useDeleteEngagementDocumentMutation,
  useUpdateEngagementDocumentMutation,
} from '@/gql/operations';
import { BaseModal, ConfirmationDialog } from '@/shared/components/Modal';
import { closeModalAtom, isModalOpenAtom, modalDataAtom, modalModeAtom } from '@/shared/state/atoms';

interface UserModalProps {
  engagementRecordId: string;
  currentUserMpId: string;
}

/**
 * Document modal wrapper that connects the generic BaseModal
 * with the DocumentForm and global modal state.
 */
const UserModal = ({ engagementRecordId, currentUserMpId }: UserModalProps) => {
  const isOpen = useAtomValue(isModalOpenAtom);
  const mode = useAtomValue(modalModeAtom);
  const data = useAtomValue(modalDataAtom) as DocumentFormData | null;
  const closeModal = useSetAtom(closeModalAtom);
  const markRecordExecuted = useSetAtom(markEngagementRecordExecutedAtom);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refetchQueries = ['GetPaginatedDocuments', 'GetFilteredClientRecords'];

  const [addDocument] = useAddEngagementDocumentMutation({ refetchQueries });
  const [updateDocument] = useUpdateEngagementDocumentMutation({ refetchQueries });
  const [deleteDocument] = useDeleteEngagementDocumentMutation({ refetchQueries });

  // Only handle view/edit/add/delete modes for documents
  if (!mode || (mode !== 'view' && mode !== 'edit' && mode !== 'add' && mode !== 'delete')) {
    return null;
  }

  const handleSave = async (formData: DocumentFormData) => {
    if (mode === 'add') {
      await addDocument({
        variables: {
          engagement_records_id: engagementRecordId,
          name: formData.documentName,
          doc_id: formData.iManageDocNumber,
          created_by: currentUserMpId,
          updated_by: currentUserMpId,
        },
      });
      markRecordExecuted(engagementRecordId);
    } else if (mode === 'edit' && data?.id) {
      await updateDocument({
        variables: {
          id: data.id,
          name: formData.documentName,
          doc_id: formData.iManageDocNumber,
          updated_by: currentUserMpId,
        },
      });
    }
    closeModal();
  };

  const handleDeleteRequest = () => {
    setConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (data?.id) {
      await deleteDocument({
        variables: { id: data.id },
      });
    }
    setConfirmDelete(false);
    closeModal();
  };

  const handleDeleteCancel = () => {
    setConfirmDelete(false);
  };

  if (confirmDelete) {
    return (
      <BaseModal open={isOpen} onClose={handleDeleteCancel} ariaLabelledBy="user-modal-title">
        <ConfirmationDialog
          title="Delete Document"
          message={`Are you sure you want to delete "${data?.documentName ?? 'this document'}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={handleDeleteCancel}
          variant="error"
        />
      </BaseModal>
    );
  }

  if (mode === 'delete') {
    return (
      <BaseModal open={isOpen} onClose={() => closeModal()} ariaLabelledBy="user-modal-title">
        <ConfirmationDialog
          title="Delete Document"
          message={`Are you sure you want to delete "${data?.documentName ?? 'this document'}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={() => closeModal()}
          variant="error"
        />
      </BaseModal>
    );
  }

  const formMode = mode === 'add' ? 'add' : mode;

  return (
    <BaseModal open={isOpen} onClose={() => closeModal()} ariaLabelledBy="user-modal-title">
      <DocumentForm
        data={mode === 'add' ? null : data}
        mode={formMode}
        onCancel={() => closeModal()}
        onSave={handleSave}
        onDelete={mode === 'edit' ? handleDeleteRequest : undefined}
        showDelete={mode === 'edit'}
      />
    </BaseModal>
  );
};

export default UserModal;
